import { INTEGRATION_SORT_FIELD } from "./integration-sort-field";
import type {
  IntegrationSortBy,
  IntegrationSortOrder,
  IntegrationSortState,
} from "../types";

import type { ActiveFilter } from "@/components/ui/v2/table/table-active-filters";

/** URL param key for the module filter (binds to the backend `hasModule` list param). */
export const MODULE_FILTER_KEY = "hasModule";
/** URL param key for the active/inactive status filter. */
export const STATUS_FILTER_KEY = "status";
/** All filter param keys this page owns (used for clearing). */
export const INTEGRATION_FILTER_KEYS = [MODULE_FILTER_KEY, STATUS_FILTER_KEY];

/** i18n key for each sort-by option's label. */
export const SORT_BY_LABEL: Record<IntegrationSortBy, string> = {
  name: "common.name",
  status: "common.status",
};

/**
 * Order options per sort field. The order axis (asc/desc) is shared, but its labels depend on
 * the field: Name reads A-Z / Z-A; Status reads Active / Inactive (the chosen status leads,
 * names stay alphabetical within each group via the list's tie-break sort).
 * For Status, "Active first" is descending (active > inactive); "Inactive first" is ascending.
 */
export const ORDER_OPTIONS: Record<
  IntegrationSortBy,
  { order: IntegrationSortOrder; label: string }[]
> = {
  name: [
    { order: "asc", label: "common.aToZ" },
    { order: "desc", label: "common.zToA" },
  ],
  status: [
    { order: "desc", label: "common.active" },
    { order: "asc", label: "common.inactive" },
  ],
};

/**
 * Derives the effective sort state from the raw URL params. Default (no params) is
 * alphabetical by name, ascending (A-Z).
 */
export function deriveSortState(
  rawOrderBy: string | undefined,
  rawDescending: boolean | undefined,
): IntegrationSortState {
  const orderBy = rawOrderBy ?? INTEGRATION_SORT_FIELD.name;
  const descending = rawDescending ?? false;
  const sortBy: IntegrationSortBy =
    orderBy === INTEGRATION_SORT_FIELD.status ? "status" : "name";
  const order: IntegrationSortOrder = descending ? "desc" : "asc";
  return { orderBy, descending, sortBy, order };
}

/**
 * Status is a single-choice filter modelled as checkboxes: exactly one selected narrows the
 * list to active/inactive; none (or both) means show all.
 */
export function deriveIsActiveFromStatus(statusFilters: string[]): boolean | null {
  return statusFilters.length === 1 ? statusFilters[0] === "active" : null;
}

type ActiveFilterLabels = {
  statusLabel: string;
  moduleLabel: string;
  statusValueLabel: (value: string) => string | undefined;
  moduleValueLabel: (value: string) => string | undefined;
};

/**
 * Builds the active-filter chips. Status chips precede module chips; each chip's `valueLabel`
 * falls back to the raw value when the resolver has no label for it.
 */
export function buildActiveFilters(
  statusValues: string[],
  moduleValues: string[],
  labels: ActiveFilterLabels,
): ActiveFilter[] {
  const statusChips: ActiveFilter[] = statusValues.map((value) => ({
    key: STATUS_FILTER_KEY,
    label: labels.statusLabel,
    value,
    valueLabel: labels.statusValueLabel(value) ?? value,
  }));
  const moduleChips: ActiveFilter[] = moduleValues.map((value) => ({
    key: MODULE_FILTER_KEY,
    label: labels.moduleLabel,
    value,
    valueLabel: labels.moduleValueLabel(value) ?? value,
  }));
  return [...statusChips, ...moduleChips];
}
